/**
 * Missatges de validació com a **claus i18n**, no com a text.
 *
 * El schema posa al `message` de Zod la clau (`form.validation.checkLetter`) i, si en
 * porta, els paràmetres (`{ letter: 'Z' }`); el formulari la tradueix en pintar-la. Els
 * paràmetres viatgen dins del mateix `message` perquè `zodResolver` de React Hook Form
 * només en copia el `message` a l'error del camp.
 */

type Params = Record<string, string | number>
type Translate = (key: string, options?: Record<string, unknown>) => string

const SEPARATOR = '|'
const FALLBACK_KEY = 'form.validation.required'

/** `zodMessage('form.validation.checkLetter', { letter: 'Z' })` → message de Zod */
export function zodMessage(key: string, params?: Params): string {
  return params ? `${key}${SEPARATOR}${JSON.stringify(params)}` : key
}

/** Separa clau i paràmetres d'un `message` fet amb `zodMessage` */
export function parseValidationMessage(message: string | undefined): { key: string; params?: Params } | null {
  if (!message) return null
  const at = message.indexOf(SEPARATOR)
  if (at === -1) return { key: message }
  try {
    return { key: message.slice(0, at), params: JSON.parse(message.slice(at + 1)) as Params }
  } catch {
    return { key: message.slice(0, at) }
  }
}

/**
 * Tradueix l'error d'un camp. Un missatge que no és una clau nostra (un text per defecte
 * de Zod, en anglès) no arriba mai a l'hoste: surt el genèric «camp obligatori».
 */
export function translateValidationMessage(t: Translate, message: string | undefined): string {
  const parsed = parseValidationMessage(message)
  if (!parsed) return ''
  if (!parsed.key.startsWith('form.')) return t(FALLBACK_KEY)
  return t(parsed.key, parsed.params)
}
